import React, { useEffect, useCallback, Fragment } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import { Tooltip } from 'react-tippy';
import {
	ApButtonTab,
	ApButtonSecondary,
	ApButtonMain,
	ApIcon,
	ApSecondaryNav,
	colors,
	ApCheckbox,
} from '@alixpartners/ui-components';

import { CONFIG } from '../routes';

import {
	TooltipContent,
	ButtonGroup,
	Divider,
	IconWrapper,
	CheckboxLabel,
	ControlGroup,
	SaveMessageWrapper,
	SaveMessageLabel,
} from './SecondaryNav.styles';

export const SecondaryNav = ({
	onUndo,
	onApply,
	onRevert,
	onShowMappedItemsChange,
	showMappedItems,
	isUndoDisabled,
	isApplyDisabled,
	isRevertDisabled,
	saveMessageType,
	prevPageUrl,
	onBackButtonClick,
	isApplying,
	isIndirect,
	canEdit,
}) => {
	const location = useLocation();

	const handleKeyPress = useCallback(
		event => {
			if (event.key.toLowerCase() !== 'z') {
				return;
			}

			if (
				!isUndoDisabled &&
				((navigator.appVersion.indexOf('Mac') !== -1 && event.metaKey) ||
					(navigator.appVersion.indexOf('Mac') === -1 && event.ctrlKey))
			) {
				onUndo();
			}
		},
		[isUndoDisabled, onUndo]
	);

	const getUndoTitle = () => {
		const metaKeyName =
			navigator.appVersion.indexOf('Mac') !== -1 ? '⌘Cmd' : 'Ctrl';

		return `Press ${metaKeyName}+Z to undo your last action`;
	};

	useEffect(
		() => {
			window.addEventListener('keydown', handleKeyPress);

			return () => {
				window.removeEventListener('keydown', handleKeyPress);
			};
		},
		[handleKeyPress]
	);

	const infoTooltipContent = isIndirect ? (
		<TooltipContent>
			Clicking 'Apply taxonomy' will apply all current mappings. Any
			transactions with categories that have not been mapped will be classified
			as the original AP taxonomy in the analysis screens. <br />
			<br />If you want to map those categories later, return to this page, map
			the categories and then click 'Apply taxonomy' again
		</TooltipContent>
	) : (
		<TooltipContent>
			Clicking 'Apply taxonomy' will apply all current mappings. Any
			transactions with categories that have not been mapped will be classified
			as 'TBC' in the analysis screens. <br />
			<br />If you want to map those categories later, return to this page, map
			the categories and then click 'Apply taxonomy' again
		</TooltipContent>
	);

	return (
		<ApSecondaryNav
			left={
				<Fragment>
					{prevPageUrl ? (
						<ApButtonSecondary
							iconName="baseline_arrow_back"
							onClick={onBackButtonClick}
						>
							Back
						</ApButtonSecondary>
					) : (
						CONFIG.map(({ label, path, disabled }) => (
							<NavLink to={path} key={path} disabled={disabled}>
								<ApButtonTab
									isSelected={location.pathname.includes(path)}
									disabled={disabled}
								>
									{label}
								</ApButtonTab>
							</NavLink>
						))
					)}
					<Divider />
					<SaveMessage type={saveMessageType} />
				</Fragment>
			}
			right={
				<Fragment>
					<ApButtonSecondary
						iconName="baseline_undo"
						disabled={isUndoDisabled || canEdit}
						onClick={onUndo}
						title={!isUndoDisabled ? getUndoTitle() : undefined}
					>
						Undo
					</ApButtonSecondary>

					<ApButtonSecondary
						iconName="backup_restore"
						onClick={onRevert}
						title="Revert to the latest applied taxonomy"
						disabled={isRevertDisabled || canEdit}
					>
						Revert
					</ApButtonSecondary>

					<Divider />
					<ControlGroup>
						<ApCheckbox
							onChange={onShowMappedItemsChange}
							checked={!showMappedItems}
						>
							<CheckboxLabel>Hide mapped items</CheckboxLabel>
						</ApCheckbox>
					</ControlGroup>
					<Divider />
					<ButtonGroup>
						<ApButtonMain
							disabled={isApplyDisabled || canEdit}
							onClick={onApply}
						>
							{isApplying ? 'Applying...' : 'Apply taxonomy'}
						</ApButtonMain>
					</ButtonGroup>
					<Tooltip
						position="bottom"
						animation="fade"
						arrow
						html={infoTooltipContent}
						trigger="mouseenter"
					>
						<IconWrapper>
							<ApIcon
								iconName="baseline_help"
								iconSize={16}
								iconColor={colors.grey6}
							/>
						</IconWrapper>
					</Tooltip>
				</Fragment>
			}
		/>
	);
};

export const SAVE_MESSAGE_TYPES = {
	success: 'success',
	fail: 'fail',
	inprogress: 'inprogress',
};

const saveMessageIconsMap = {
	[SAVE_MESSAGE_TYPES.fail]: 'cloud_off',
	[SAVE_MESSAGE_TYPES.success]: 'cloud_done',
	[SAVE_MESSAGE_TYPES.inprogress]: 'cloud_upload',
};

const saveMessageTextMap = {
	[SAVE_MESSAGE_TYPES.fail]: 'Autosave failed',
	[SAVE_MESSAGE_TYPES.success]: 'Saved',
	[SAVE_MESSAGE_TYPES.inprogress]: 'Saving...',
};

export const SaveMessage = ({ type }) => {
	const iconName = saveMessageIconsMap[type] || 'cloud_queue';
	const messageText = saveMessageTextMap[type] || 'Saved';

	return (
		<SaveMessageWrapper>
			<ApIcon iconName={iconName} iconColor={colors.grey4} />
			<SaveMessageLabel>{messageText}</SaveMessageLabel>
		</SaveMessageWrapper>
	);
};

export default SecondaryNav;
